import { Injectable, OnInit } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { RoleEnum } from '../enums/role-enum';
import { IToken } from '../interfaces/IToken.interface';
import { KeycloakService } from 'keycloak-angular';

@Injectable()
export class UserContext {
  public role: RoleEnum | undefined;
  private contextualizedToken: IToken | undefined;

  constructor(private keycloakService: KeycloakService) {
    this.initToken();
  }

  private initToken() {
    // get token from Keycloak
    if (this.keycloakService.getKeycloakInstance().authenticated) {
      const token = this.keycloakService.getKeycloakInstance().token;
      if (token) {
        this.contextualizeToken(token);
      }
    }
  }

  getToken() {
    // Get token from Keycloak
    if (this.keycloakService.getKeycloakInstance().authenticated) {
      const token = this.keycloakService.getKeycloakInstance().token;
      return token;
    }
  }

  isLoggedIn() {
    return this.keycloakService.isLoggedIn();
  }

  username() {
    //console.log("Username: ", this.contextualizedToken?.preferred_username);
    if (this.contextualizedToken) {
      return this.contextualizedToken.preferred_username;
    }
    return " ";
  }

  logout() {
    this.keycloakService.logout();
  }

  login() {
    this.keycloakService.login();
  }

  redirectToLoginPage(): Promise<void> {
    return this.keycloakService.login();
  }

  // Get user roles synchronously from the token
  getUserRoles(): string[] {
    if (!this.contextualizedToken) {
      return [];
    }
    
    return this.extractRolesFromToken(this.contextualizedToken);
  }

  private contextualizeToken(token: string) {
    if (token) {
      this.contextualizedToken = this.parseToken(token);
      if (this.contextualizedToken) {
        // Extract roles from token
        const roles = this.extractRolesFromToken(this.contextualizedToken);
        
        // Set the primary role if found
        if (roles && roles.length > 0) {
          this.role = roles[0] as RoleEnum;
        }
      }
    }
  }

  private extractRolesFromToken(tokenData: any): string[] {
    // Get roles from resource_access.prod-assa-backend-client.roles
    if (tokenData.resource_access && 
        tokenData.resource_access['prod-assa-backend-client'] && 
        tokenData.resource_access['prod-assa-backend-client'].roles) {
      return tokenData.resource_access['prod-assa-backend-client'].roles;
    }
    
    // Fallback to realm_access.roles if above path doesn't exist
    if (tokenData.realm_access && tokenData.realm_access.roles) {
      return tokenData.realm_access.roles;
    }
    
    // If no roles found in expected locations, check if roles is directly in the token
    if (tokenData.roles) {
      return Array.isArray(tokenData.roles) ? tokenData.roles : [tokenData.roles];
    }
    
    return [];
  }

  private parseToken(token: string): IToken {
    let jwtHelper: JwtHelperService = new JwtHelperService();
    const tokenPayload: any = jwtHelper.decodeToken(token);
    return tokenPayload;
  }

  isAuthenticated(): boolean {
    return !!this.contextualizedToken;
  }

  getRole(): RoleEnum {
    if (this.role === undefined) {
      throw new Error('Role is not defined');
    }
    return this.role;
  }

  fullName(): string {
    if (this.contextualizedToken) {
      return `${this.contextualizedToken.given_name} ${this.contextualizedToken.family_name}`;
    } else {
      return "";
    }
  }

  isAdminOrAnnotator(): boolean {
    return this.isAdministrator() || this.isAnnotator();
  }

  isAdministrator(): boolean {
    const roles = this.getUserRoles()
    return roles[0].includes(RoleEnum.Administrator.toString());
  }
  
  isAnnotator(): boolean {
    const roles = this.getUserRoles();
    return roles[0].includes(RoleEnum.Annotator.toString());
  }
  
  // Get a specific role by checking the token directly
  hasRole(roleName: RoleEnum | string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(roleName.toString());
  }
}